import appStore from '../utils/state';
import type { CartState } from '../types';
import CartItem from './cart-item';
import { consumePendingPostEnterCartRefresh, refreshCartFromServer } from '../utils/syncFreeGift';

function getCartItemIdentity(cartItemEl: Element): string | null {
  // Variant + nested + properties — not Shopify's line `key`. The key's hash
  // includes discount applications, so a loyalty/threshold gift re-hash would
  // look like a remove+add and flash the card. `data-key` stays on the element
  // for `/cart/change.js`. Multiset counts still handle discount-split giftboxes
  // that share the same variant and properties.
  const variantId = cartItemEl.getAttribute('data-variant-id');
  if (!variantId) return null;

  const nested = cartItemEl.getAttribute('data-nested') === 'true' ? '1' : '0';
  const fingerprint = cartItemEl.getAttribute('data-identity-fingerprint') ?? '';
  return `${variantId}|${nested}|${fingerprint}`;
}

function buildIdentityCounts(list: HTMLUListElement | null): Map<string, number> {
  const counts = new Map<string, number>();
  if (!list) return counts;

  for (const cartItemEl of list.querySelectorAll('cart-item')) {
    const identity = getCartItemIdentity(cartItemEl);
    if (!identity) continue;
    counts.set(identity, (counts.get(identity) ?? 0) + 1);
  }

  return counts;
}

/**
 * Owns the line-items list inside the cart drawer only (the giftbox modal
 * has its own, separate, simpler reconciliation — see `giftbox-modal.ts`).
 *
 * On every cart update, it diffs the currently rendered `<cart-item>` cards
 * against the server-rendered section by stable identity (variant + nested +
 * properties, not Shopify's line `key`), animates out any card that
 * disappeared, swaps the DOM for the fresh server markup, then animates in
 * any card that's genuinely new.
 */
export default class CartItems extends HTMLElement {
  private container: HTMLDivElement | null = null;
  private list: HTMLUListElement | null = null;
  private lastReconciledRevision = 0;

  private unsubscribeFromCartUpdating: (() => void) | null = null;
  private unsubscribeFromCartUpdated: (() => void) | null = null;

  private onCartUpdatingBound = this.onCartUpdating.bind(this);
  private onCartUpdatedBound = this.onCartUpdated.bind(this);

  connectedCallback(): void {
    this.list = this.querySelector('[data-cart-items-ref="list"]');
    this.container = this.querySelector('[data-cart-items-ref="container"]');

    this.addEventListeners();
    this.subscribe();

    console.log('CartItems connected');
  }

  addEventListeners(): void {}

  removeEventListeners(): void {}

  subscribe(): void {
    this.unsubscribeFromCartUpdating = appStore.subscribeKey('cartIsUpdating', this.onCartUpdatingBound);
    this.unsubscribeFromCartUpdated = appStore.subscribeKey('cart', this.onCartUpdatedBound);
  }

  unsubscribe(): void {
    if (this.unsubscribeFromCartUpdating) {
      this.unsubscribeFromCartUpdating();
      this.unsubscribeFromCartUpdating = null;
    }
    if (this.unsubscribeFromCartUpdated) {
      this.unsubscribeFromCartUpdated();
      this.unsubscribeFromCartUpdated = null;
    }
  }

  onContinueShoppingButtonClick(): void {
    appStore.actions.closeCartModal();
  }

  onCartUpdating(_cartIsUpdating: boolean): void {}

  /**
   * Horizon-style reconcile with exit animations: hide any live card missing from the
   * next section (threshold free gift, nested offered product, clicked remove), then
   * replace the list and show newly added cards. Matching uses a stable identity
   * (variant + nested + properties) so quantity-only updates and Shopify key rotation
   * from discounts do not trigger exit/enter; duplicate presentment rows (discount-split
   * giftboxes) are still handled via occurrence counts.
   */
  onCartUpdated(cart: CartState): void {
    if (cart.revision <= this.lastReconciledRevision) return;
    void this.reconcileList(cart);
  }

  private async reconcileList(cart: CartState): Promise<void> {
    if (!this.list) return;

    const revision = cart.revision;
    this.lastReconciledRevision = revision;

    const nextList = cart.data?.drawerSection?.querySelector('[data-cart-items-ref="list"]') as HTMLUListElement | null;

    const previousCounts = buildIdentityCounts(this.list);
    const nextCounts = buildIdentityCounts(nextList);
    const remainingNext = new Map(nextCounts);
    const departing = this.collectDepartingItems(this.list, remainingNext);

    if (departing.length > 0) {
      await Promise.all(departing.map((item) => item.hideAsync()));
    }

    // A newer cart update started while we were animating — skip this replace.
    if (revision !== this.lastReconciledRevision) return;
    if (!this.list) return;

    this.replaceListFromSection(this.list, nextList);
    await this.animateNewItems(this.list, previousCounts);

    // A newer cart update started while we were animating — skip the deferred refresh.
    if (revision !== this.lastReconciledRevision) return;

    if (consumePendingPostEnterCartRefresh()) {
      void refreshCartFromServer();
    }
  }

  /**
   * Multiset diff in DOM order: consume next-list slots with cards that are
   * not already exiting, so an optimistic `hide()` on a clicked twin is the
   * occurrence that departs rather than the sibling that should remain.
   */
  private collectDepartingItems(list: HTMLUListElement, remainingNext: Map<string, number>): CartItem[] {
    const departing: CartItem[] = [];
    const removing: CartItem[] = [];

    for (const cartItemEl of list.querySelectorAll('cart-item')) {
      const item = cartItemEl as CartItem;
      const identity = getCartItemIdentity(item);
      if (!identity) continue;

      if (item.isRemoving) {
        removing.push(item);
        continue;
      }

      const remaining = remainingNext.get(identity) ?? 0;
      if (remaining > 0) {
        remainingNext.set(identity, remaining - 1);
        continue;
      }

      departing.push(item);
    }

    for (const item of removing) {
      const identity = getCartItemIdentity(item);
      if (!identity) continue;

      const remaining = remainingNext.get(identity) ?? 0;
      if (remaining > 0) {
        remainingNext.set(identity, remaining - 1);
        continue;
      }

      departing.push(item);
    }

    return departing;
  }

  /**
   * Fully swaps the list content with the server-rendered section rather
   * than patching individual `<cart-item>` nodes in place — the section is
   * the single source of truth for quantities/prices/discount splits, and
   * patching every field in place would just re-derive what the server
   * already computed. `document.importNode` is required because `nextList`
   * comes from a detached section parsed via `DOMParser`/template, not the
   * live document.
   */
  private replaceListFromSection(list: HTMLUListElement, nextList: HTMLUListElement | null): void {
    list.replaceChildren();

    if (!nextList) return;

    for (const child of Array.from(nextList.children)) {
      list.appendChild(document.importNode(child, true));
    }
  }

  /**
   * Runs after the full replace, so every card in `list` is a brand new
   * element — even ones that were already present before. Re-derives which
   * ones are "actually new" (vs. a quantity-only update or key rotation that
   * just happened to recreate the same card) by walking the same identity
   * multiset used for the pre-replace diff, and only plays the enter animation
   * on the surplus.
   */
  private async animateNewItems(list: HTMLUListElement, previousCounts: Map<string, number>): Promise<void> {
    const remainingPrevious = new Map(previousCounts);
    const entering: Promise<void>[] = [];

    for (const cartItemEl of list.querySelectorAll('cart-item')) {
      const identity = getCartItemIdentity(cartItemEl);
      if (!identity) continue;

      const remaining = remainingPrevious.get(identity) ?? 0;
      if (remaining > 0) {
        remainingPrevious.set(identity, remaining - 1);
        continue;
      }

      entering.push((cartItemEl as CartItem).showAsync());
    }

    if (entering.length > 0) {
      await Promise.all(entering);
    }
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.unsubscribe();
  }
}

customElements.define('cart-items', CartItems);
